import { useEffect, useState } from 'react';
import { Copy, Trash2, X } from 'lucide-react';
import type { Invite } from '@archer/shared';
import { useAuth } from '../auth/store';

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
}

export const ShareDialog = ({ open, onClose, projectId }: ShareDialogProps) => {
  const api = useAuth((s) => s.api);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !api) return;
    void api.listInvites(projectId).then((r) => setInvites(r.invites)).catch(() => undefined);
  }, [open, api, projectId]);

  if (!open) return null;

  const create = async (role: 'editor' | 'viewer') => {
    if (!api) return;
    setBusy(true);
    try {
      const { invite } = await api.createInvite(projectId, role);
      setInvites((s) => [invite, ...s]);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (token: string) => {
    if (!api) return;
    setBusy(true);
    try {
      await api.revokeInvite(projectId, token);
      setInvites((s) => s.filter((i) => i.token !== token));
    } finally {
      setBusy(false);
    }
  };

  const shareUrl = (token: string): string => {
    const base = window.location.origin;
    return `${base}/invites/${token}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur">
      <div className="card w-full max-w-lg">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold">Share project</h3>
          <button className="tool-btn" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-3 text-xs text-slate-400">
          Generate invite links. Anyone with the link can join this project until you revoke it.
        </p>
        <div className="mb-4 flex gap-2">
          <button className="btn-secondary flex-1" disabled={busy} onClick={() => create('viewer')}>
            + Viewer link
          </button>
          <button className="btn-secondary flex-1" disabled={busy} onClick={() => create('editor')}>
            + Editor link
          </button>
        </div>
        <div className="space-y-2">
          {invites.length === 0 && <p className="text-sm text-slate-500">No active invites yet.</p>}
          {invites.map((inv) => {
            const url = shareUrl(inv.token);
            return (
              <div
                key={inv.token}
                className="flex items-center gap-2 rounded-md border border-slate-800 bg-slate-950 p-2"
              >
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${
                    inv.role === 'editor' ? 'bg-archer-500/20 text-archer-300' : 'bg-slate-800 text-slate-400'
                  }`}
                >
                  {inv.role}
                </span>
                <code className="flex-1 truncate text-xs text-slate-300">{url}</code>
                <button
                  className="tool-btn"
                  onClick={() => {
                    void navigator.clipboard.writeText(url).then(() => {
                      setCopied(inv.token);
                      setTimeout(() => setCopied(null), 1500);
                    });
                  }}
                >
                  {copied === inv.token ? <Check /> : <Copy className="h-4 w-4" />}
                </button>
                <button className="tool-btn text-red-400" onClick={() => void revoke(inv.token)}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

import { Check } from 'lucide-react';
